import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Assessment={
  versionId:string;
  instrumentName:string;
  title:string;
  versionLabel:string;
  description?:string|null;
  gradeMin?:number|null;
  gradeMax?:number|null;
  estimatedMinutes?:number|null;
  assignedCount:number;
  completedCount:number;
};

type Administration={
  administrationId:string;
  versionId:string;
  status:string;
  assignedAt?:string|null;
  startedAt?:string|null;
  completedAt?:string|null;
};

type Learner={
  learnerId:string;
  studentKey?:string|null;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  administrations:Administration[];
};

type Workspace={
  assessments:Assessment[];
  learners:Learner[];
};

type StudentFilter="NOT_ASSIGNED"|"IN_PROGRESS"|"COMPLETED"|"ALL";

function statusLabel(status:string){
  if(status==="ASSIGNED")return "대기";
  if(status==="IN_PROGRESS")return "진행 중";
  if(status==="COMPLETED")return "완료";
  return status;
}

function gradeRange(item:Assessment){
  if(item.gradeMin && item.gradeMax)return `${item.gradeMin}~${item.gradeMax}학년`;
  if(item.gradeMin)return `${item.gradeMin}학년 이상`;
  if(item.gradeMax)return `${item.gradeMax}학년 이하`;
  return "전 학년";
}

function learnerEligible(learner:Learner,assessment:Assessment|null){
  if(!assessment)return false;
  const grade=Number(learner.gradeLevel);
  if(!Number.isFinite(grade))return true;
  if(assessment.gradeMin && grade<assessment.gradeMin)return false;
  if(assessment.gradeMax && grade>assessment.gradeMax)return false;
  return true;
}

export function CenterAssessmentsPage({
  client,
  schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const initialParams=new URLSearchParams(window.location.search);
  const [workspace,setWorkspace]=useState<Workspace|null>(null);
  const [selectedVersion,setSelectedVersion]=useState("");
  const [selectedLearners,setSelectedLearners]=useState<string[]>([]);
  const [query,setQuery]=useState("");
  const [grade,setGrade]=useState(initialParams.get("grade") || "전체");
  const [classLabel,setClassLabel]=useState(initialParams.get("class") || "전체");
  const [filter,setFilter]=useState<StudentFilter>("NOT_ASSIGNED");
  const [busy,setBusy]=useState(false);
  const [message,setMessage]=useState("");
  const [error,setError]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_assessment_workspace",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setWorkspace(next);
      setSelectedVersion((current)=>
        next.assessments.some((item)=>item.versionId===current)
          ? current
          : next.assessments[0]?.versionId || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const selectedAssessment=useMemo(
    ()=>workspace?.assessments.find((item)=>item.versionId===selectedVersion) || null,
    [workspace,selectedVersion]
  );

  useEffect(()=>{
    setSelectedLearners([]);
    setMessage("");
  },[selectedVersion]);

  const grades=useMemo(
    ()=>["전체",...Array.from(new Set((workspace?.learners || []).map((item)=>item.gradeLevel).filter(Boolean) as string[])).sort((a,b)=>Number(a)-Number(b))],
    [workspace]
  );

  const classes=useMemo(
    ()=>["전체",...Array.from(new Set((workspace?.learners || []).map((item)=>item.classLabel).filter(Boolean) as string[])).sort()],
    [workspace]
  );

  const visibleLearners=useMemo(()=>{
    if(!workspace || !selectedAssessment)return [];
    const q=query.trim().toLowerCase();

    return workspace.learners.filter((learner)=>{
      if(!learnerEligible(learner,selectedAssessment))return false;
      if(grade!=="전체" && learner.gradeLevel!==grade)return false;
      if(classLabel!=="전체" && learner.classLabel!==classLabel)return false;
      if(q && ![learner.displayName,learner.studentKey,learner.gradeLevel,learner.classLabel].filter(Boolean).join(" ").toLowerCase().includes(q))return false;

      const latest=learner.administrations.find((item)=>item.versionId===selectedVersion);
      if(filter==="NOT_ASSIGNED")return !latest || latest.status==="COMPLETED";
      if(filter==="IN_PROGRESS")return Boolean(latest && ["ASSIGNED","IN_PROGRESS"].includes(latest.status));
      if(filter==="COMPLETED")return latest?.status==="COMPLETED";
      return true;
    });
  },[workspace,selectedAssessment,selectedVersion,query,grade,classLabel,filter]);

  const selectableLearners=visibleLearners.filter((learner)=>{
    const latest=learner.administrations.find((item)=>item.versionId===selectedVersion);
    return !latest || latest.status==="COMPLETED";
  });

  const allSelected=selectableLearners.length>0 && selectableLearners.every((item)=>selectedLearners.includes(item.learnerId));

  function toggle(id:string){
    setSelectedLearners((current)=>
      current.includes(id) ? current.filter((item)=>item!==id) : [...current,id]
    );
  }

  function toggleAll(){
    setSelectedLearners(allSelected ? [] : selectableLearners.map((item)=>item.learnerId));
  }

  async function assign(){
    if(!selectedAssessment || selectedLearners.length===0)return;
    setBusy(true);
    setError("");
    setMessage("");

    const {data,error}=await client.rpc("center_assign_assessment_batch",{
      p_school_id:schoolId,
      p_version_id:selectedAssessment.versionId,
      p_learner_ids:selectedLearners
    });

    setBusy(false);

    if(error){
      setError(error.message);
      return;
    }

    const result=data as {assignedCount:number;skippedCount:number;errorCount:number};
    setMessage(
      `${result.assignedCount}명에게 검사를 보냈습니다.`+
      (result.skippedCount ? ` · 이미 진행 중 ${result.skippedCount}명 제외` : "")+
      (result.errorCount ? ` · 오류 ${result.errorCount}명` : "")
    );
    setSelectedLearners([]);
    setRefresh((value)=>value+1);
  }

  if(!workspace)return <p className="muted">진로검사 목록을 불러오는 중…</p>;

  return (
    <section className="teacher-assessment-page">
      <div className="section-heading teacher-clean-heading">
        <div>
          <p className="eyebrow">진로검사</p>
          <h2>검사를 고르고 학생에게 바로 보내세요</h2>
          <p className="route-description">검사 선택 → 학년·반 선택 → 학생 선택 → 보내기 순서로 끝납니다.</p>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="assessment-pick-layout">
        <aside className="assessment-picker">
          <div className="panel-title">
            <div><strong>1. 검사 선택</strong><span>{workspace.assessments.length}개 사용 가능</span></div>
          </div>
          <div className="assessment-picker-list">
            {workspace.assessments.map((assessment)=>(
              <button
                key={assessment.versionId}
                className={assessment.versionId===selectedVersion ? "active" : ""}
                onClick={()=>setSelectedVersion(assessment.versionId)}
              >
                <span>
                  <strong>{assessment.title}</strong>
                  <small>{gradeRange(assessment)} · 약 {assessment.estimatedMinutes || "-"}분</small>
                </span>
                <b>{assessment.completedCount}/{assessment.assignedCount}</b>
              </button>
            ))}
          </div>
        </aside>

        <div className="assessment-roster">
          {selectedAssessment ? (
            <>
              <header className="assessment-selected-head">
                <div>
                  <p className="eyebrow">{selectedAssessment.instrumentName}</p>
                  <h3>{selectedAssessment.title}</h3>
                  <p>{selectedAssessment.description || "학생에게 배정할 진로검사입니다."}</p>
                </div>
                <div className="assessment-duration">
                  <span>예상 소요</span>
                  <strong>{selectedAssessment.estimatedMinutes || "-"}분</strong>
                </div>
              </header>

              <div className="teacher-roster-toolbar">
                <div className="teacher-filter-row">
                  <input
                    className="teacher-search"
                    value={query}
                    onChange={(event)=>setQuery(event.target.value)}
                    placeholder="학생 이름 검색"
                  />
                  <select value={grade} onChange={(event)=>setGrade(event.target.value)}>
                    {grades.map((item)=><option key={item} value={item}>{item==="전체" ? "전체 학년" : item+"학년"}</option>)}
                  </select>
                  <select value={classLabel} onChange={(event)=>setClassLabel(event.target.value)}>
                    {classes.map((item)=><option key={item} value={item}>{item==="전체" ? "전체 반" : item+"반"}</option>)}
                  </select>
                </div>

                <div className="badge-filter-row">
                  {([
                    ["NOT_ASSIGNED","보낼 학생"],
                    ["IN_PROGRESS","진행 중"],
                    ["COMPLETED","완료"],
                    ["ALL","전체"]
                  ] as Array<[StudentFilter,string]>).map(([value,label])=>(
                    <button
                      key={value}
                      className={filter===value ? "filter-chip active" : "filter-chip"}
                      onClick={()=>setFilter(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="assessment-selection-bar">
                <label>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  현재 목록 전체 선택
                </label>
                <button className="primary" disabled={busy || selectedLearners.length===0} onClick={assign}>
                  {busy ? "보내는 중…" : `선택한 ${selectedLearners.length}명에게 검사 보내기`}
                </button>
              </div>

              <div className="teacher-student-table">
                {visibleLearners.map((learner)=>{
                  const latest=learner.administrations.find((item)=>item.versionId===selectedVersion);
                  const canSelect=!latest || latest.status==="COMPLETED";
                  return (
                    <label key={learner.learnerId} className="teacher-student-row">
                      <input
                        type="checkbox"
                        disabled={!canSelect}
                        checked={selectedLearners.includes(learner.learnerId)}
                        onChange={()=>toggle(learner.learnerId)}
                      />
                      <span className="teacher-student-name">
                        <strong>{learner.displayName}</strong>
                        <small>
                          {learner.gradeLevel ? learner.gradeLevel+"학년" : "학년 미정"}
                          {learner.classLabel ? " · "+learner.classLabel+"반" : ""}
                          {learner.studentKey ? " · "+learner.studentKey : ""}
                        </small>
                      </span>
                      <span className={latest?.status==="COMPLETED" ? "status-pill good" : "status-pill"}>
                        {latest ? statusLabel(latest.status) : "미배정"}
                      </span>
                    </label>
                  );
                })}
                {!visibleLearners.length && <p className="case-empty">조건에 맞는 학생이 없습니다.</p>}
              </div>
            </>
          ) : (
            <p className="case-empty">사용 가능한 검사가 없습니다.</p>
          )}
        </div>
      </div>
    </section>
  );
}
